import React, { useContext, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Chip, Grid, Stack, Tab, Tabs, useMediaQuery } from '@mui/material'
import { IHeadCell } from '../../../../shared/interfaces/table-display.model'
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display'
import gaiaTheme from '../../../../root-components/theme'
import { ISubsidiary } from '../../../../shared/services/subsidiary/subsidiary.model'
import SubsidiaryService from '../../../../shared/services/subsidiary/subsidiary.service'
import { LoadingPage } from '../../loading-page'
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container'
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading'
import { IconName } from '../../../../shared/interfaces/icon-picker.model'
import { SearchBar } from '../../../presentation-components/display-elements-components/search-bar'
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels'
import { useDebounce } from '../../../../hooks/use-debounce-hook'
import { UserDetailsContext } from '../../../../context/user-details/user-details'
import { a11yProps } from '../../../../shared/utils/misc-shared-utils'

export const OrganisationSubsidiariesPage = () => {
  const navigate = useNavigate();
  //Services
  const subsidiaryService = SubsidiaryService.getInstance()
  //Context
  const screenLabelsContext = useContext(ScreenLabelsContext)
  const userDetailsContext = useContext(UserDetailsContext)
  const roles = userDetailsContext.userDetails?.roles || []
  const disableCreate = roles.includes('analyst') && !roles.includes('admin')
  //State
  const [subsidiaries, setSubsidiaries] = useState<ISubsidiary[]>([]);
  const [value, setValue] = React.useState(0);
  const [searchQuery, setSearchQuery] = useState('')
  //Table State
  const [selected, setSelected] = useState<string[]>([]);
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [change, setChange] = useState<number>(0)
  const [orderBy, setOrderBy] = useState<string>()
  const [order, setOrder] = useState<'asc' | 'desc'>()
  //Loading State
  const [hasPageMounted, setHasPageMounted] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  //Hooks
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  //Ref
  const requestId = useRef<number>(0)
  //Layout
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));
  //Use Effects
  useEffect(() => {
    requestId.current++
    const currentRequestId = requestId.current
    subsidiaryService.getAllSubsidiariesWithPagination(value === 0, debouncedSearchQuery, inputPage, perPage, orderBy, order)
      .then(response => {
        if (currentRequestId === requestId.current) {
          setIsLoading(false)
          setTotalRecords(response.count);
          setSubsidiaries(response.rows)
        }
      })
      .catch(err => {
        if (currentRequestId === requestId.current) {
          setIsLoading(false)
        }
        console.log(err)
      })
  }, [inputPage, perPage, change])

  useEffect(() => {
    if (hasPageMounted) {
      if (inputPage === 0) {
        setChange(change + 1)
      } else {
        setInputPage(0)
      }
    }
    setHasPageMounted(true)
  }, [order, orderBy, debouncedSearchQuery, value])

  //Table Contents
  const headCells: IHeadCell[] = [
    { id: 'name', numeric: false, label: screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' }), sortable: true, isHeading: true },
    { id: 'subsidiaryReference', numeric: false, label: 'Reference(s)', sortable: true },
    { id: 'country', numeric: false, label: 'Country', sortable: true },
  ]

  const rows = subsidiaries.map(subsidiary => {
    return {
      id: subsidiary.id,
      name: (
        <Stack display='flex' flexDirection='row' alignItems='center'>
          {subsidiary.name}
          {subsidiary.isPrimary && <Chip variant='outlined' label={isMobileMode ? 'Primary' : `Primary ${screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })}`} color='primary' sx={{ ml: 1 }} />}
        </Stack>
      ),
      subsidiaryReference: subsidiary?.subsidiaryReference ? subsidiary.subsidiaryReference.split(',').join(', ') : '',
      country: (
        <Stack display='flex' flexDirection='row' alignItems='center' gap={1}>
          <img
            loading="lazy"
            width="20"
            src={subsidiary.country.imageUrl}
            alt={`${subsidiary.country.name} flag`}
          />
          {subsidiary.country.name}
        </Stack>
      ),
    }
  });

  //Table Event Handlers 
  const handleOrderChange = (orderBy: string, order: 'asc' | 'desc',) => {
    setOrderBy(orderBy)
    setOrder(order)
  };

  const handleSelected = (selectedRowId: string[]) => {
    if (selectedRowId[0] !== undefined) {
      navigate(`/organisation/subsidiaries/${selectedRowId[0]}`)
    }
  }

  //Component Event Handlers
  const changeTabHandler = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    setIsLoading(true)
    setSearchQuery('')
  };

  return (
    <PageOuterContainer>
      <PageHeading
        title={screenLabelsContext.getLabel('subsidiary', { plural: true, capitalize: 'first' })}
        includeActionButton={true}
        actionButtonIcon={IconName.Add}
        actionButtonHandler={() => navigate('/organisation/subsidiaries/add-new-subsidiary')}
        actionButtonText={`Add ${screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })}`}
        disableActionButton={disableCreate}
        disabledActionButtonTooltip={`You do not have permission to add ${screenLabelsContext.getLabel('subsidiary', { plural: true })}`}
        hasTabs={true}
      />
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={value} onChange={changeTabHandler} aria-label={`${screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })} status tabs`}>
            <Tab label="Active" {...a11yProps(0)} />
            <Tab label="Inactive" {...a11yProps(1)} />
          </Tabs>
        </Box>
        <Grid container sx={{ mb: 2 }}>
          <Grid item xl={4} lg={4} md={6} sm={12} xs={12}
            sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', width: '100%' }}>
            <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
          </Grid>
        </Grid>
        <LoadingPage pageReady={!isLoading}>
          {subsidiaries &&
            <TableDisplay
              onOrderChange={handleOrderChange}
              isMobileMode={isMobileMode}
              headCells={headCells}
              rows={rows}
              handleSelected={handleSelected}
              selected={selected}
              setSelected={setSelected}
              inputPage={inputPage}
              setInputPage={setInputPage}
              totalRecords={totalRecords}
              perPage={perPage}
              setPerPage={setPerPage} />}
        </LoadingPage>
      </Box>
    </PageOuterContainer>
  )
}
